import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { endOfDay, startOfDay } from 'date-fns';

import { convertToMask, fileDownload, formatPayloadDate } from '../../utils/common';
import Pagination from '../../components/form/Pagination';
import FormSelect from '../../components/form/FormSelect';
import AdminHeader from '../../components/header/AdminHeader';
import FormDateInput from '../../components/form/FormDateInput';
import whatsappIcon from '../../assets/images/icons/whatsapp.svg';

import { callHistoryGet } from '../../store/slices/callHistorySlice';
import CallexLoader from '../../components/CallexLoader';
import { setIsDialNumber } from '../../store/slices/dialSlice';
import FormInput from '../../components/form/FormInput';
import { useUserDetails } from '../../store/slices/authSlice';
import {
  setActiveHelpDetails,
  whatsappAddDetails
} from '../../store/slices/tenantSlices/helpDeskSlice';
import { useTimezone } from '../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

const CallHistory = () => {
  const timezone = useTimezone();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useUserDetails();
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });
  const [headerFields, setHeaderFields] = useState({
    default: [],
    crm_fields: []
  });

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    type: 'all',
    destination_number: '',
    start_date: startOfDay(today),
    end_date: endOfDay(today)
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    type: Yup.string().required('Type is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = { ...values, destination_number: values?.destination_number || undefined };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(callHistoryGet(payload)).unwrap();
      if (response) {
        setHeaderFields({
          ...headerFields,
          default: response?.header_fields?.default || [],
          crm_fields: response?.header_fields?.crm_fields || []
        });
        setReportData(response?.data || []);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    onSubmit();
  }, [values?.limit, values?.page]);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderData, data, filename: 'Call_hostory.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = { ...values };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(callHistoryGet(payload)).unwrap();

      if (response) {
        const combinedHeaders = [
          ...(response?.header_fields?.default || []),
          ...(response?.header_fields?.crm_fields || [])
        ];

        const headers = combinedHeaders.map((val) => ({ label: val?.label, key: val?.name }));

        setCsvReportData(response?.data || []);
        setCsvHeaderData(headers);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  const addNumber = async (primaryNumber) => {
    try {
      const payload = { from: `91${primaryNumber}@c.us`, name: primaryNumber };
      const response = await dispatch(whatsappAddDetails(payload)).unwrap();
      if (response && response?.message) {
        dispatch(
          setActiveHelpDetails({
            ...response?.data,
            id: response?.data?._id,
            name: response?.data?.from_name,
            activeTab: response?.data?.assignedToId ? 'mine' : 'unassigned'
          })
        );
        navigate('/help-desk');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title={t('callHistory')}
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={reportData?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label={t('fromDate')}
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  label={t('toDate')}
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  label={t('destinationNumber')}
                  placeholder={t('enterDestinationNumber')}
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="destination_number"
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label={t('type')}
                  placeholder="Select Type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="type"
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Answered', value: 'answered' },
                    { label: 'Unanswered', value: 'not_answered' }
                  ]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit" disabled={isLoading}>
                {t('search')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.default?.length}>{t('default')}</th>
                </tr>
                <tr>
                  {headerFields?.default?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.default?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] ? (
                                <Icon.Download
                                  size={16}
                                  onClick={() => {
                                    const link = `cdrs_report/download?filename=${val[value?.name]}`;
                                    fileDownload(link);
                                  }}
                                />
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        if (value?.name === 'crm_number') {
                          return (
                            <td key={idx}>
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                {user?.process_details?.numberMasking === 'true'
                                  ? convertToMask(val[value?.name] || '-')
                                  : val[value?.name] || '-'}{' '}
                                <div className="d-flex align-items-center gap-2">
                                  <Icon.PhoneForwarded
                                    size={14}
                                    className="cursor-pointer text-green"
                                    onClick={() => dispatch(setIsDialNumber(val[value?.name]))}
                                  />
                                  {user?.process_details?.whatsapp_chat && (
                                    <img
                                      src={whatsappIcon}
                                      width={16}
                                      height={16}
                                      className="cursor-pointer"
                                      onClick={() => addNumber(val[value?.name])}
                                    />
                                  )}
                                </div>
                              </div>
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.crm_fields?.length}>{t('crm')}</th>
                </tr>
                <tr>
                  {headerFields?.crm_fields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.crm_fields?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={paginationDetails?.total}
              limit={paginationDetails?.limit}
              page={paginationDetails?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          {t('noDataAvailable')}
        </Card>
      )}
    </div>
  );
};

export default CallHistory;
